import React from 'react';
import styled from 'styled-components';
import Fonts from '../../../../../ui/global/Fonts';
import Colors from '../../../../../ui/global/Colors';
import Icon from '../../../../../ui/global/Icon';
import type { AIToolData } from '../helpers/Types';
import { defaultConfig } from '../../../../../utils/config';
import { getUIColors, resolveAgentKeyFromTool, shouldColorize } from '../helpers/subAgent';
import TruncatedPathLabel, { LabelChip, isLikelyFilePath } from './FilePath';

interface AIToolProps {
  tool: AIToolData;
}

const AITool: React.FC<AIToolProps> = ({ tool }) => {
  const { name, icon, textInProgress, textCompleted, label, status, params } = tool;

  const subAgentKey: string | undefined = React.useMemo(() => resolveAgentKeyFromTool(tool), [tool]);

  const { bg: chipBg, fg: chipText } = React.useMemo(() => defaultConfig.STREAM_SUB_AGENT_COLORS && shouldColorize(name) ? getUIColors(subAgentKey) : {}, [subAgentKey, name]);
  const iconColor = chipText ? Colors.grey3 : Colors.grey4;
  
  // Helper to process general templates (dot-paths supported). Missing -> empty string.
  const processTemplate = React.useCallback((template: string | undefined) => {
    if (!template) return template;
    const getByPath = (obj: any, path: string) => {
      if (!obj) return undefined;
      return path.split('.').reduce((acc: any, k: string) => (acc != null ? acc[k] : undefined), obj);
    };
    const rendered = template.replace(/\{([^}]+)\}/g, (_match, key) => {
      const value = getByPath(params, String(key).trim());
      return value == null ? '' : String(value);
    });
    return rendered;
  }, [params]);

  // Label-specific processor: if any placeholder unresolved, hide entire label
  const processLabelTemplate = React.useCallback((template: string | undefined) => {
    if (!template) return template;
    const getByPath = (obj: any, path: string) => {
      if (!obj) return undefined;
      return path.split('.').reduce((acc: any, k: string) => (acc != null ? acc[k] : undefined), obj);
    };
    const placeholders = Array.from(template.matchAll(/\{([^}]+)\}/g)).map(m => (m[1] || '').trim());
    for (const key of placeholders) {
      const value = getByPath(params, key);
      if (value == null) return undefined;
    }
    return template.replace(/\{([^}]+)\}/g, (_match, key) => String(getByPath(params, String(key).trim())));
  }, [params]);
  
  // Process templates for textInProgress and textCompleted
  const processedTextInProgress = React.useMemo(() => 
    processTemplate(textInProgress), 
    [textInProgress, processTemplate]
  );
  
  const processedTextCompleted = React.useMemo(() => 
    processTemplate(textCompleted), 
    [textCompleted, processTemplate]
  );
  
  // Determine display text based on status
  const displayText = (status === 'complete' || status === 'error') ? processedTextCompleted : processedTextInProgress;
  
  // Process label template with params if provided
  const processedLabel = React.useMemo(() => {
    return processLabelTemplate(label);
  }, [label, processLabelTemplate, params]);

  const shouldTruncatePath = React.useMemo(() => {
    return defaultConfig.STREAM_PARALLEL_SUBAGENTS && 
           subAgentKey != null && 
           isLikelyFilePath(processedLabel);
  }, [subAgentKey, processedLabel]);

  const getStatusIcon = () => {
    if (status === 'called') return 'loading';
    if (status === 'complete' || status === 'error') {
      return defaultConfig.SHOW_TOOL_SPECIFIC_ICONS ? icon : 'check-slim';
    }
    return defaultConfig.SHOW_TOOL_SPECIFIC_ICONS ? icon : 'check-slim';
  };

  return (
    <ToolContainer>
      <ToolContent>
        {/* Icon */}
        <IconContainer>
          <Icon 
            icon={getStatusIcon() || 'computer'} 
            color={iconColor}
            size={14} 
          />
        </IconContainer>

        {/* Status Text */}
        <StatusText $status={status}>
          {displayText}
        </StatusText>

        {/* Label */}
        {processedLabel && (
          shouldTruncatePath ? (
            <TruncatedPathLabel path={processedLabel} $bg={chipBg} $fg={chipText} />
          ) : (
            <LabelChip $bg={chipBg} $fg={chipText}>{processedLabel}</LabelChip>
          )
        )}

        {defaultConfig.DEBUG_STREAM_TOOLS && status === 'error' && (
          <LabelChip $fg={Colors.red}>error</LabelChip> // $bg={Colors.redDark} 
          // <LabelChip>
          //   <Icon icon="warning" color={Colors.red} size={12} />
          // </LabelChip>
        )}
      </ToolContent>
    </ToolContainer>
  );
};

export default AITool;

const ToolContainer = styled.div`
  // background: ${Colors.black+60};
  border-radius: 6px;
  align-self: flex-start;
  width: auto;
  // padding-left: 5px;
`;

const ToolContent = styled.div`
  display: flex;
  align-items: center;
  // padding: 4px 12px;
  padding: 0 12px 0 0;
  gap: 4px;
  overflow: hidden;
  min-width: 0;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: -2px;
`;

const StatusText = styled.div<{ $status: string }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 13px;
  color: ${Colors.grey3};
  flex-shrink: 0;
  white-space: nowrap;
`;

